"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { getCollectionListings, getCollectionTraits } from "../api";
import type { CollectionListingsResponse, CollectionTraitsResponse } from "../types";

/** The key every listing change invalidates (a list or a cancel on an NFT of the collection). */
export const COLLECTION_LISTINGS_KEY = "collection-listings";

/**
 * `GET /collections/{contract}/listings` — a page at a time, the previous
 * page kept on screen while the next loads. Short-lived on purpose: a list
 * or a cancel must show on the next read.
 */
export function useCollectionListings(contractAddress, lang, params) {
  const $ = _c(10);
  const limit = params?.limit;
  const page = params?.page;
  let t0;
  let t1;
  if ($[0] !== contractAddress || $[1] !== lang || $[2] !== limit || $[3] !== page) {
    t0 = [COLLECTION_LISTINGS_KEY, contractAddress, lang, limit, page];
    t1 = () => getCollectionListings(contractAddress, {
      limit,
      page
    }, lang);
    $[0] = contractAddress;
    $[1] = lang;
    $[2] = limit;
    $[3] = page;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  const t2 = params?.enabled !== false && Boolean(contractAddress);
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      placeholderData: _temp,
      staleTime: 0
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useQuery(t3);
}

/** The collection's traits and their values with counts — the items filter's catalogue. */
function _temp(previous) {
  return previous;
}
export function useCollectionTraits(contractAddress, networkId) {
  const $ = _c(10);
  const t0 = networkId ?? null;
  let t1;
  if ($[0] !== contractAddress || $[1] !== t0) {
    t1 = ["collection-traits", contractAddress, t0];
    $[0] = contractAddress;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== contractAddress || $[4] !== networkId) {
    t2 = () => getCollectionTraits(contractAddress, networkId);
    $[3] = contractAddress;
    $[4] = networkId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = Boolean(contractAddress);
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: 300000
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return useQuery(t4);
}